import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CameraIcon, CheckIcon, MapPinIcon, QrCodeIcon, ScanLineIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
const ease = [0.23, 1, 0.32, 1] as const;
type Mode = 'choix' | 'scan' | 'scan-resultat' | 'signalement' | 'transmis';
interface FoundPetDialogProps {
  open: boolean;
  onClose: () => void;
}
export function FoundPetDialog({
  open,
  onClose
}: FoundPetDialogProps) {
  const [mode, setMode] = useState<Mode>('choix');
  const [photo, setPhoto] = useState(false);
  const [place, setPlace] = useState('');
  const close = () => {
    onClose();
    window.setTimeout(() => {
      setMode('choix');
      setPhoto(false);
      setPlace('');
    }, 250);
  };
  return <AnimatePresence data-id="element-54" data-mp="2rexmk4fjr6uj">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-55" data-mp="2ri1yxp3618wa">
          <button type="button" aria-label="Close" onClick={close} className="absolute inset-0 bg-ville-navy/40" data-id="element-56" data-mp="2whntpj01kgmx" />
          <motion.div role="dialog" aria-modal="true" aria-label="Animal found" initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-57" data-mp="3ah5ocf00qo47">
            <button type="button" onClick={close} aria-label="Close" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-58" data-mp="3p4ie6fwuzg0e">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-59" data-mp="2zsufz7jyfs4e" />
            </button>

            <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-60" data-mp="34sywuyoryov5">Animal found</h2>

            <AnimatePresence mode="wait" data-id="element-61" data-mp="39jyu651t38y6">
              <motion.div key={mode} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} transition={{
            duration: 0.22,
            ease
          }} className="mt-5" data-id="element-62" data-mp="39l04g7azzreu">
                {mode === 'choix' && <div data-id="element-63" data-mp="3sib1dl95q8r4">
                    <p className="text-[15px] text-slate-600" data-id="element-64" data-mp="3sijhdm0ktu6j">
                      Thanks for helping. Two ways to find its owner.
                    </p>

                    <div className="mt-4 space-y-3" data-id="element-65" data-mp="1t7jqqp8u5cbf">
                      <button type="button" onClick={() => setMode('scan')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-66" data-mp="1y5bh3etu0qdi">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ville-indigo-soft text-ville-indigo" data-id="element-67" data-mp="1bx61lu2txoqh">
                          <QrCodeIcon className="h-5 w-5" aria-hidden="true" data-id="element-68" data-mp="29h966x4cv27u" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-69" data-mp="1zlfkoo8nrydx">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-70" data-mp="1zjuo6t2sa5df">Scan its collar</span>
                          <span className="block text-sm text-slate-500" data-id="element-71" data-mp="1vjkkjn2grr1q">
                            The animal is wearing a licence tag with a code
                          </span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-72" data-mp="k6rwam8vtkqf" />
                      </button>

                      <button type="button" onClick={() => setMode('signalement')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-73" data-mp="2cslj4n3l6ahj">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" data-id="element-74" data-mp="2vr7gbqvzfhg6">
                          <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-75" data-mp="2hqmxiuq2hlmo" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-76" data-mp="2lqykwf9te01u">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-77" data-mp="16brkteazdu8r">Report it to the Town</span>
                          <span className="block text-sm text-slate-500" data-id="element-78" data-mp="1aee8d30mct05">No tag, or the code is unreadable</span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-79" data-mp="12vwvcmtjaw2f" />
                      </button>
                    </div>
                  </div>}

                {mode === 'scan' && <div data-id="element-80" data-mp="17swx4ljcpcnq">
                    <div className="relative grid h-44 place-items-center overflow-hidden rounded-2xl bg-slate-100" data-id="element-81" data-mp="17tpxn4rpv5xf">
                      <ScanLineIcon className="h-12 w-12 text-slate-400" aria-hidden="true" data-id="element-82" data-mp="k1hvkuzeezdj" />
                      <motion.span className="absolute inset-x-8 h-0.5 bg-ville-indigo" initial={{
                  top: '20%'
                }} animate={{
                  top: ['20%', '80%', '20%']
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-83" data-mp="f6c6jnap94l8" />
                    </div>

                    <ol className="mt-5 space-y-3" data-id="element-84" data-mp="3sicopiqe95ef">
                      {['Approach the animal gently, without startling it.', 'Look for the licence tag on its collar.', 'Line up the QR code on the tag inside the frame above.'].map((etape, index) => <li key={etape} className="flex gap-3 text-[15px] text-slate-600" data-id="element-85" data-mp="ztk8ymmoqh4d" data-mpi={index}>
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-xs font-bold text-ville-indigo" data-id="element-86" data-mp="zvek5985muv9">
                            {index + 1}
                          </span>
                          {etape}
                        </li>)}
                    </ol>

                    <button type="button" onClick={() => setMode('scan-resultat')} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-87" data-mp="h3c8ydea86se">
                      Simulate scanning the tag
                    </button>
                    <button type="button" onClick={() => setMode('signalement')} className="mt-3 w-full text-sm font-semibold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-88" data-mp="339f4imtrdd9s">
                      The code is unreadable — report it to the Town
                    </button>
                  </div>}

                {mode === 'scan-resultat' && <div className="text-center" data-id="element-89" data-mp="2fdt8nt6ue5u2">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-90" data-mp="ik99xx66grya">
                      <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-91" data-mp="dkwpfj2s3wyv" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-92" data-mp="3im80bqmr9wc3">Licence recognized</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-93" data-mp="3njrr1q0n41kx">
                      This animal is licensed with the Town of Bonnyville. Its owner has just been notified that it was found. Your contact details are not shared with them: the Town makes the connection.
                    </p>
                    <p className="mt-3 rounded-2xl bg-slate-50 px-5 py-3 text-sm text-slate-600" data-id="element-94" data-mp="2ztdw9rqo7omn">
                      Licence BON-•••21 · notice sent just now
                    </p>
                    <button type="button" onClick={close} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-95" data-mp="2qvle8ftke2vk">
                      Done
                    </button>
                  </div>}

                {mode === 'signalement' && <div data-id="element-96" data-mp="2nbtc9lk1pbeb">
                    <p className="text-[15px] text-slate-600" data-id="element-97" data-mp="2ncct0yxyltqr">
                      A photo and a location are enough. Your report opens a request with the right department.
                    </p>

                    <button type="button" onClick={() => setPhoto(true)} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${photo ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-dashed border-slate-300 text-ville-navy hover:border-ville-indigo'}`} data-id="element-98" data-mp="2x9hgr37gdx9o">
                      {photo ? <CheckIcon className="h-5 w-5" data-id="element-99" data-mp="21j0oj7qpv7ky" /> : <CameraIcon className="h-5 w-5" data-id="element-100" data-mp="1wliok6uog2p3" />}
                      {photo ? 'Photo attached' : 'Take a photo of the animal'}
                    </button>

                    <div className="mt-4" data-id="element-101" data-mp="1smjde4icr9p7">
                      <label htmlFor="lieu-trouve" className="block text-sm font-bold text-ville-navy" data-id="element-102" data-mp="1nm3z9aq8xy66">
                        Where did you find it?
                      </label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 px-4 focus-within:border-ville-indigo" data-id="element-103" data-mp="2ga9lk1wy7v84">
                        <MapPinIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-104" data-mp="2bhq6t1miyu1q" />
                        <input id="lieu-trouve" type="text" value={place} onChange={event => setPlace(event.target.value)} placeholder="My current location or an address" className="w-full border-0 bg-transparent py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-105" data-mp="26jooiiwxayrb" />
                      </div>
                    </div>

                    <button type="button" onClick={() => setMode('transmis')} disabled={!photo} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-106" data-mp="3gkkd854g25kx">
                      Send to the Town
                    </button>
                  </div>}

                {mode === 'transmis' && <div className="text-center" data-id="element-107" data-mp="h0q4lcxeddi7">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-108" data-mp="2d78z630zw70q">
                      <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-109" data-mp="296glmxl3bfyn" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-110" data-mp="2mxbsk4c13mzl">
                      Report sent
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-111" data-mp="2wtf0xrxh4huz">
                      Your report has been sent to animal control. You can follow its progress under My Requests.
                    </p>
                    <p className="mt-3 font-mono text-sm text-slate-500" data-id="element-112" data-mp="35tl8kx6hqnm8">#REQ-2903</p>
                    <Link to="/my-requests/req-2903" onClick={close} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-113" data-mp="30wuyqrla4o99">
                      Track my request
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-114" data-mp="39br60f02lafc" />
                    </Link>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}